import { CommonModule, DatePipe } from '@angular/common';
import { InitialsPipe } from 'src/app/shared/pipes/initials.pipe';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import store from 'store2';
import { SeachClinicPatientListModel } from 'src/app/models/clinicParms/search-clinic-patient-list-model';
import { ClinicPatientService } from 'src/app/services/clinic.patient.service';
import { UserService } from 'src/app/services/user.service';
import { ToastHelper } from '../common/toaster.helper';
import { GetPatient } from 'src/app/models/get-patient';
import { EmailLogService } from 'src/app/services/patient.email.service';
import { Subject, take, takeUntil } from 'rxjs';
import { EmailLog } from 'src/app/models/email-log-model';
import { MessageService } from '../common/newMessage.service';
import { PatientChatComponent } from "../patients-list/patient-profile/patient-chat/patient-chat.component";
import { PaymentHistoryService } from 'src/app/services/payment-history.service';
import { RetrievePaymentRequestInput } from 'src/app/models/clinicParms/payment_profile_request';
import { MenuPermissionFlatDto } from 'src/app/models/clinicParms/operation-item';

@Component({
  selector: 'app-communication',
  standalone: true,
  imports: [CommonModule, FormsModule, PatientChatComponent, InitialsPipe],
  providers: [DatePipe],
  templateUrl: './communication.component.html',
  styleUrls: ['./communication.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommunicationComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  constructor(
    private _clinicPatientService: ClinicPatientService,
    private userService: UserService,
    private _toastr: ToastHelper,
    private _patientEmailService: EmailLogService,
    private messageService: MessageService,
    private datePipe: DatePipe,
    private paymentHistoryService: PaymentHistoryService,
    private cdr: ChangeDetectorRef
  ) {
    const now = new Date();
    console.log(this.datePipe.transform(now, 'dd-MM-yyyy hh:mm a'));
  }

  userpermissions: MenuPermissionFlatDto[] = JSON.parse(store('userPermissions') || '[]');
  ComponentName: string = 'Communication';
  ViewPermission: boolean = false;
  ngOnInit(): void {
    this.ViewPermission = this.userpermissions.find(u => u.cname == this.ComponentName && u.permissionName == 'View')? true : false;
    this.loadPatients();
  }

  @ViewChild(PatientChatComponent) patientChat!: PatientChatComponent | undefined;

  // -------------------
  // LEFT SECTION (CHAT)
  // -------------------

  newMessage: string = '';

  leftTabs = [
    { id: 'chat', label: 'Chat', icon: 'chat_bubble' }
  ];
  activeLeftTab = 'chat';

  switchLeftTab(tabId: string) {
    this.activeLeftTab = tabId;
  }

  // Patients
  users: GetPatient[] = [];
  patients: GetPatient[] = [];
  searchString: string = '';

  selectedUser: GetPatient = null;
  selectedChatPatientId: number = null;
  arePatientsLoading = false;

  // -------------------
  // RIGHT SECTION (APPOINTMENTS + PAYMENTS)
  // -------------------

  rightTabs = [
    { id: 'appointment-reminders', label: 'Reminders', icon: 'event' },
    { id: 'payment-notifications', label: 'Payments', icon: 'payments' }
  ];
  activeRightTab = 'appointment-reminders';

  switchRightTab(tabId: string) {
    this.activeRightTab = tabId;
  }

  appointmentReminders: any[] = [];
  paymentNotifications: any[] = [];

  // -------------------
  // BOTTOM SECTION (MAIL)
  // -------------------

  patientMails: EmailLog[] = [];
  loadingMails = false;

  loadMailsForPatient(patientId: number): void {
    this.loadingMails = true;
    this._patientEmailService.getPatientEmails(patientId).pipe(take(1)).subscribe({
      next: (res) => {
        this.patientMails = res.sort((a, b) => b.emailLogId - a.emailLogId);
        this.extractAppointmentReminders(this.patientMails);
        this.loadingMails = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error(err);
        this.loadingMails = false;
        this.cdr.markForCheck();
      }
    });
  }

  private loadPatientPayments(): void {
    if (!this.selectedUser) return;

    // const request: RetrievePaymentRequestInput = {
    //   patientID: this.selectedUser.patientID,
    //   profileId: null,
    //   branchId: this.selectedUser.branchID,
    //   acctid: null,
    //   retref: null
    // };

    let request = new RetrievePaymentRequestInput();
    request.patientID = this.selectedUser.patientID;
    request.branchId = this.selectedUser.branchID;

    this.paymentHistoryService.GetPaymentHistoryByPatient(request).pipe(takeUntil(this.destroy$)).subscribe({
      next: (payments: any[]) => {
        this.paymentNotifications = payments
          .sort((a, b) => new Date(b.invoiceDate).getTime() - new Date(a.invoiceDate).getTime())
          .map((p, index) => ({
            id: index + 1,
            patient: p.patient
              ? `${p.patient.firstName || ''} ${p.patient.lastName || ''}`.trim()
              : 'Unknown',
            amount: p.amount || 0,
            date: p.invoiceDate ? new Date(p.invoiceDate) : (p.addOn ? new Date(p.addOn) : null),
            paymentMode: p.paymentMode || 'N/A'
          }));
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error loading patient payments', err);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // helper to normalize payment status
  private mapPaymentStatus(status: any, retref?: any, details?: any): string {
    if (!status) return 'Pending';
    const s = status.toString().toLowerCase();

    if (s.includes('success') || s.includes('paid') || retref?.toString().startsWith('OK')) {
      return 'Paid';
    }
    if (s.includes('pending')) {
      return 'Pending';
    }
    if (s.includes('fail') || s.includes('decline')) {
      return 'Failed';
    }
    if (details?.toLowerCase().includes('refund')) {
      return 'Refunded';
    }

    return 'Pending'; // default fallback
  }

  private extractAppointmentReminders(mails: EmailLog[]): void {
    this.appointmentReminders = mails
      .filter(mail => mail.emailSubject?.toLowerCase().includes('appointment reminder'))
      .map((mail, index) => {
        // try to extract appointment date from email body
        const appointmentDate = this.extractAppointmentDate(mail.emailBody);

        // fallback extraction (if regex fails)
        let reminderDate: string = '';
        const match = mail.emailSubject.match(/(\d{4}-\d{2}-\d{2})\s+(\d{1,2}:\d{2}\s?(AM|PM)?)/i);

        const formatDateTime = (d: Date): string => {
          const datePart = d.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          });
          const timePart = d.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });
          return `${datePart} – ${timePart}`; // ✅ dash before time
        };

        if (match) {
          reminderDate = formatDateTime(new Date(`${match[1]} ${match[2]}`));
        } else if (mail.queuedTime) {
          reminderDate = formatDateTime(new Date(mail.queuedTime));
        }

        return {
          id: index + 1,
          patient: this.selectedUser ? `${this.selectedUser.firstName} ${this.selectedUser.lastName}` : 'Unknown',
          appointmentDate: appointmentDate, // ✅ new formatted field
          reminderDate: reminderDate,
          status: mail.emailBody?.toLowerCase().includes('confirmed') ? 'Confirmed' : 'Pending'
        };
      });
  }

  private extractAppointmentDate(body: string): string | null {
    const regex = /(Sunday|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday),\s+[A-Za-z]+\s+\d{1,2},\s+\d{4}\s+\d{1,2}:\d{2}:\d{2}\s+(AM|PM)/;
    const match = body.match(regex);

    if (match) {
      const rawDate = match[0];
      const parsedDate = new Date(rawDate);

      // Format with Angular DatePipe
      const formatted = this.datePipe.transform(parsedDate, 'MMM d, y – h:mm a');
      return formatted ?? rawDate;
    }
    return null;
  }

  // -------------------
  // PATIENTS & CHAT LOGIC
  // -------------------

  trackById(index: number, item: any) {
    return item.id;
  }

  trackByIndex(index: number): number { return index; }
  trackByUser(index: number, item: any): any { return item.patientID; }
  trackByEmailLog(index: number, item: any): any { return item.emailLogId; }

  loadPatients() {
    let demoSearchPatientListModel = new SeachClinicPatientListModel();
    demoSearchPatientListModel.branchID = store('userBranches');
    demoSearchPatientListModel.offset = 0;
    demoSearchPatientListModel.nextRows = 0;
    demoSearchPatientListModel.totalNum = 0;
    demoSearchPatientListModel.allowedBranches = store('userBranches');
    demoSearchPatientListModel.sortExpression = "Addon DESC";
    demoSearchPatientListModel.multiCampaign = -1;
    demoSearchPatientListModel.patientStatusID = "1";

    this.arePatientsLoading = true;
    this._clinicPatientService.GetPatients(demoSearchPatientListModel).pipe(takeUntil(this.destroy$)).subscribe({
      next: res => {
        this.patients = res;
        this.getUnreadMessages();
        this.users = this.patients;
        this.arePatientsLoading = false;
        this.cdr.markForCheck();
      },
      error: err => {
        console.error('Error loading patients:', err);
        this.arePatientsLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  filterPatient(): void {
    const search = this.searchString.toLowerCase();
    this.users = this.patients.filter(x =>
      x.firstName?.toLowerCase().includes(search) ||
      x.lastName?.toLowerCase().includes(search)
      // x.email?.toLowerCase().includes(search)
    );
  }

  selectChatUser(user: GetPatient) {
    this.selectedChatPatientId = null;

    setTimeout(() => {
      if (this.selectedUser == user) {
        // deselect
        this.selectedUser = null;
        this.selectedChatPatientId = null;
        this.patientMails = null;
        this.cdr.markForCheck();
      } else {
        // select
        this.selectedChatPatientId = user.patientID;
        this.selectedUser = user;
        this.cdr.markForCheck();

        // load mails for bottom section
        this.loadMailsForPatient(user.patientID);

        // mark messages read
        this.readMessagesCall(user.patientID);

        this.loadPatientPayments();
      }
    }, 500);
  }

  // -------------------
  // CHAT INPUT HANDLER
  // -------------------

  sendMessage() {
    if (!this.newMessage.trim() || !this.selectedChatPatientId) return;

    // integrate with your chat service here
    console.log("Sending message:", this.newMessage, "to patient:", this.selectedChatPatientId);

    // clear after send
    this.newMessage = '';
  }

  // -------------------
  // MESSAGES
  // -------------------

  newMessagesList: NewChatMessageLog[] = [];

  getUnreadMessages(): void {
    this._clinicPatientService.GetPatientUnreadMessagesCount().pipe(take(1)).subscribe({
      next: (res) => {
        if (res.length > 0) {
          this.newMessagesList = res;
          this.newMessagesList.forEach(x => {
            let patient = this.patients.find(p => p.patientID === x.patientID);
            if (patient) patient.unreadMessages = x.newMessageCount;
          });
          this.patients.sort((a, b) => (b.unreadMessages || 0) - (a.unreadMessages || 0));
        }
        this.cdr.markForCheck();
      },
      error: (err) => console.error(err)
    });
  }

  readMessagesCall(patientId: number): void {
    this.messageService.setNewMessageAvailable(false);
    this._clinicPatientService.UnreadMessagesReadCall(patientId).pipe(take(1)).subscribe({
      next: (res) => {
        if (res) this.loadPatients();
      }
    });
  }
}

export class NewChatMessageLog {
  patientID!: number;
  twillioChatId!: string;
  newMessageCount!: number;
}
